import { Checkbox, Group } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { updateProfile } from '../../fetchHooks/updateProfile';
import type { EnabledServicesTypes } from '../../fetchHooks/useUserProfile';

interface UserServicesCheckboxProps {
  userId?: number;
  services?: EnabledServicesTypes[];
  style?: React.CSSProperties;
}

const ALL_SERVICES: EnabledServicesTypes[] = ['salary', 'myday', 'passwords', 'simplified', 'health'];

export const UserServicesCheckbox = ({ userId, services = [], style }: UserServicesCheckboxProps) => {
  const [selectedServices, setSelectedServices] = useState<EnabledServicesTypes[]>(services);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();

  const isServicesUpdated = JSON.stringify(selectedServices.sort()) === JSON.stringify(services.sort());

  const handleServiceChange = (service: EnabledServicesTypes, checked: boolean) => {
    const newServices = checked ? [...selectedServices, service] : selectedServices.filter((s) => s !== service);

    setSelectedServices(newServices);

    // Clear previous timer if any
    if (debounceTimer) clearTimeout(debounceTimer);

    // Set a new timer to send the PUT request after 2 seconds
    const newDebounceTimer = setTimeout(() => {
      updateProfile({ services: newServices, id: userId }, () => {
        void queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      });
    }, 2000);

    setDebounceTimer(newDebounceTimer as NodeJS.Timeout);
  };

  useEffect(() => {
    // Clear the timer when the component unmounts
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  // Update local state when props change
  useEffect(() => {
    setSelectedServices(services);
  }, [services]);

  if (!userId) return null;

  return (
    <div style={{ marginBottom: '1rem', ...style }}>
      <div style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
        Enabled Services
        {!isServicesUpdated && <span style={{ color: 'orange', marginLeft: '0.5rem' }}>(Unsaved changes)</span>}
      </div>
      <Group>
        {ALL_SERVICES.map((service) => (
          <Checkbox
            key={service}
            label={service.charAt(0).toUpperCase() + service.slice(1)}
            checked={selectedServices.includes(service)}
            onChange={(event) => handleServiceChange(service, event.currentTarget.checked)}
            size="sm"
          />
        ))}
      </Group>
    </div>
  );
};

export default UserServicesCheckbox;
